import * as React from 'react';
import * as ReactDOM from 'react-dom';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '@/utils/utils';
import { usePortalHost } from '@/common/usePortalHost';

const TooltipProvider = TooltipPrimitive.Provider;
const Tooltip = TooltipPrimitive.Root;
const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 6, side, ...props }, ref) => {
  // Into the window it shows in (the detached timeline's, or this one's)
  const portalHost = usePortalHost();

  return ReactDOM.createPortal(
    <TooltipPrimitive.Content
      ref={ref}
      side={side}
      sideOffset={sideOffset}
      className={cn(
        'animate-in ease-lureo-easing-enter fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:ease-lureo-easing-exit data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-lureo-layer-tooltip overflow-visible whitespace-nowrap rounded-lureo-radius-tooltip bg-lureo-tooltip px-4 py-2 font-lureo-font-ui text-lureo-14 font-light text-lureo-on-tooltip shadow-lureo-shadow-overlay leading-5',
        className
      )}
      {...props}
    >
      {props.children}
      <TooltipPrimitive.Arrow className="fill-lureo-tooltip"/>
    </TooltipPrimitive.Content>,
    portalHost.body
  );
});

TooltipContent.displayName = TooltipPrimitive.Content.displayName;

interface WithTooltipProps {
  /** What it says on hover; with nothing to say, the element renders alone. */
  content: React.ReactNode;
  side?: React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>['side'];
  /** A long hint (a sentence or two), wrapped at a readable width rather than kept to one line. */
  wrap?: boolean;
  /** One element that takes a ref (a button, a span); a disabled control goes inside a span, which takes the hover. */
  children: React.ReactElement;
}

/** A hint on hover, drawn in the theme: what a native title would say, never the browser's own box. */
function WithTooltip({ content, side, wrap = false, children }: WithTooltipProps) {
  const [open, setOpen] = React.useState(false);
  const trigger = React.useRef<HTMLButtonElement>(null);
  // Hovering or keyboard focus shows the hint; focus handed back by a menu or select closing after a click does not
  const onOpenChange = (next: boolean) => {
    const element = trigger.current;
    if (next && element && document.activeElement === element && !element.matches(':focus-visible') && !element.matches(':hover')) return;
    setOpen(next);
  };
  if (content === undefined || content === null || content === '' || content === false) return children;
  return (
    <Tooltip open={open} onOpenChange={onOpenChange}>
      <TooltipTrigger ref={trigger} asChild>{children}</TooltipTrigger>
      <TooltipContent side={side} className={wrap ? 'max-w-xs whitespace-normal' : undefined}>
        {content}
      </TooltipContent>
    </Tooltip>
  );
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider, WithTooltip };